// 261010 Epic 2 / P3d — "Phiếu AI chấm" ở đầu trang chi tiết video, bước 3 (Đạo diễn & diễn xuất) và 4 (Quay phim). Chủ dữ liệu `GET step-reviews?eventId=` cho video này.
// Hai giám khảo AI chấm MÙ, code gộp kết luận, người phân xử. Phiếu chỉ HIỆN + ghi sổ: không đổi `state` bước, không chặn bước sau, KHÔNG có nút "AI tự sửa" (User 25/10: chạy thử trước).
// Chữ / luật quyết ở stepReviewPolicy.ts (có test); poll ở stepReviewPoller.ts. Sau MỌI thao tác ghi và khi phiếu chạy xong gọi `onActionDone` để trang tải lại `workflow-status` (alert + chuông).
// 261010 P6: thêm `CLIP_REVIEW` — MỘT thẻ cho MỘT bản clip (`candidateClipId`); dữ liệu của video có thể do nơi khác sở hữu (`controller`, xem useStepReviews.ts) để các dòng bản clip dùng chung.
import React, { useEffect, useMemo, useState } from 'react';
import type { FilmRunningJob } from '../../types/film';
import type { LeaderDecision, PromptPick, ReviewSnapshotMeta, StepReview, StepReviewItem, StepReviewKey } from '../../types/filmStepReview';
import { clipAudioOf, clipItemOf, clipReviewTitle, frameTargetOf } from './clipReviewPolicy';
import ReviewMediaViewer, { type ViewerTarget } from './ReviewMediaViewer';
import { evidenceViews, mediaRows, type EvidenceView } from './reviewMediaPolicy';
import {
  CL_REVIEW, CLIP_REVIEW_NOTE, PR_REVIEW, PROMPT_REVIEW_NOTE, REVIEW_TITLE, STORYBOARD_REVIEW_NOTE, SB_REVIEW, agreedView, budgetFor, budgetLine, canHighlightEvidence, decisionGate, decisionLine, groupByFrame, groupByShot, groupByTime, historyRows, isRunActive, itemOf, judgeColumns, latestChip,
  mediaSnapshotOf, nonBlockingChip, promptPickOf, reviewCostLine, reviewPhase, runButton, shotAnchorId, staleNote, unverifiedNote, validateDecision, verdictChip, writerLine, type FindingView,
} from './stepReviewPolicy';
import { useStepReviews, type StepReviewsController } from './useStepReviews';
import './stepReview.css';

interface Props {
  planId: string;
  eventId: string;
  stepKey: StepReviewKey;
  /** Việc nền của plan (từ `workflow-status`) — khoá nút khi máy tự chạy chặng đang chạy hoặc đang có job chấm đúng video + bước (+ đúng bản clip với `CLIP_REVIEW`). */
  jobs?: FilmRunningJob[];
  onActionDone: () => void;
  /** P5d (AI chấm prompt): bấm câu trích của finding ⇒ bước Prompt đánh dấu câu đó trong khung prompt. Không truyền ⇒ không có nút. */
  onPickEvidence?: (pick: PromptPick) => void;
  /** P6 (`CLIP_REVIEW`): bản clip đang xem phiếu; null ⇒ video chưa có bản clip nào tạo xong để chấm. Các bước khác không truyền. */
  candidateClipId?: string | null;
  /** P6: nhãn "Bản N" ghép vào tiêu đề — "AI chấm clip · Bản N". */
  clipLabel?: string;
  /** P6: chủ dữ liệu do nơi khác sở hữu (trang bước Video chia cho cả các dòng bản clip). Không truyền ⇒ thẻ tự nạp + poll như P3d/P5c/P5d. */
  controller?: StepReviewsController;
}

const when = (iso: string): string => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleString('vi-VN') : '';
};

/** Cuộn tới thẻ shot (neo `wf-shot-N` do bước Đạo diễn / Quay phim đặt). Không thấy neo (shot đã đổi / bị xoá) ⇒ không làm gì. */
const scrollToShot = (order: number) => {
  document.getElementById(shotAnchorId(order))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

/** `plain` (AI chấm prompt): bước Prompt không có neo `wf-shot-N` ⇒ chỉ là chữ, không hứa "cuộn tới shot". */
const ShotLink: React.FC<{ f: Pick<FindingView, 'shotOrder' | 'shotLabel'>; plain?: boolean }> = ({ f, plain }) => (
  f.shotOrder > 0 && !plain
    ? <button type="button" className="wf-rv-shot" onClick={() => scrollToShot(f.shotOrder)} title="Cuộn tới shot này">{f.shotLabel}</button>
    : <span className="wf-rv-note">{f.shotLabel}</span>
);

/** Duyệt B (P5c): ngữ cảnh để dựng bằng chứng hình — bước, danh sách ảnh đã gửi, và mở khung xem ảnh. P6: `clipReview` + `clipTitle` để mở khung hình đã trích của clip. */
interface FindingCtx {
  stepKey: StepReviewKey; snapshot: ReviewSnapshotMeta | null | undefined; onOpen: (t: ViewerTarget) => void; onPick?: (quote: string) => void; // onPick: card đã gói thêm bản chữ của phiếu (promptPickOf)
  clipReview?: StepReview | null; clipTitle?: string;
  /** P6: file âm thanh giám khảo nghe đã nghe (url null ⇒ BE không lưu được file). */
  clipAudio?: { status: 'JUDGED' | 'NO_TRACK' | null; url: string | null };
}

const evidenceTarget = (e: EvidenceView): ViewerTarget | null => (e.src ? {
  src: e.src,
  title: `${e.frameLabel} — ảnh đã gửi cho giám khảo`,
  lines: [`${e.frameLabel} · ${e.rangeLabel}`, e.observation, e.region ? '' : 'Không có vùng đánh dấu cho bằng chứng này (không đoán vị trí frame từ lưới).'].filter(Boolean),
  region: e.region,
} : null);

const unopenableNote = (e: EvidenceView, snapshot: ReviewSnapshotMeta | null | undefined): string => {
  if (e.media) return 'Đường dẫn ảnh không an toàn — không mở.';
  return snapshot ? 'Ảnh này không có trong bộ ảnh đã gửi — không mở được.' : 'Phiếu chưa kèm danh sách ảnh đã gửi — không mở được.';
};

/** `source` (P6): finding của giám khảo NGHE (V4) không có khung hình — hiện giây + nút "Nghe" (file âm thanh BE đã lưu) thay vì "Xem khung hình". */
const Finding: React.FC<{ f: FindingView; ctx: FindingCtx; showShot?: boolean; source?: 'VISUAL' | 'AUDIO' }> = ({ f, ctx, showShot, source }) => {
  const [listening, setListening] = useState(false);
  const sb = ctx.stepKey === SB_REVIEW;
  const pr = ctx.stepKey === PR_REVIEW;
  const cl = ctx.stepKey === CL_REVIEW;
  const heard = cl && source === 'AUDIO';
  const evs = sb ? evidenceViews(f.media, ctx.snapshot) : [];
  const frame = cl && !heard && !f.unverified ? frameTargetOf(f, ctx.clipReview, ctx.clipTitle ?? REVIEW_TITLE.CLIP_REVIEW) : null; // finding unverified ⇒ mốc / khung BE đã nói là sai, không mở
  const audioUrl = ctx.clipAudio?.url ?? null;
  return (
    <div className={`wf-rv-f${f.blocking ? ' blocking' : ''}${f.unverified ? ' unverified' : ''}`} data-testid="wf-rv-f">
      <span className="code" title={f.criterion}>{f.code}</span>
      <span className={`sev ${f.sevLabel === 'Nặng' ? 'blocking' : 'advisory'}`}>{f.sevLabel}</span>
      {showShot ? (sb ? <> · <span className="wf-rv-frame">{f.frameLabel}</span></> : cl ? <> · <span className="wf-rv-frame">{f.timeLabel}</span></> : <> · <ShotLink f={f} plain={pr} /></>) : null}
      <div className="wf-rv-note">{f.criterion.replace(/^[A-Z]\d · /, '')}</div>
      {cl && f.sevLabel === 'Gợi ý' ? <div className="wf-rv-note">Chỉ là gợi ý, không phải lỗi nặng.</div> : null}
      {f.unverified ? <div className="unv">{unverifiedNote(ctx.stepKey, f.unverifiedReason)}</div> : null}
      {f.evidence ? <blockquote>{f.evidence}</blockquote> : null}
      {pr && ctx.onPick && canHighlightEvidence(f)
        ? <button type="button" className="wf-rv-shot wf-rv-pick" onClick={() => ctx.onPick?.(f.quote)} title="Đánh dấu câu này trong khung prompt">Xem câu này trong prompt</button> : null}
      {cl && !heard && f.timestampS !== null && !f.unverified ? (
        frame
          ? <button type="button" className="wf-rv-shot" onClick={() => ctx.onOpen(frame)} title="Mở khung hình đã gửi cho giám khảo">Xem khung hình</button>
          : <div className="wf-rv-note">Chưa có khung hình lưu cho mốc này.</div>) : null}
      {heard && f.timestampS !== null && !f.unverified ? (
        audioUrl
          ? (<>
            <button type="button" className="wf-rv-shot" onClick={() => setListening((o) => !o)} title="Nghe đoạn âm thanh đã gửi cho giám khảo, tua tới mốc giây này">{listening ? 'Ẩn trình nghe' : 'Nghe'}</button>
            {listening ? <audio data-testid="wf-rv-audio" controls preload="none" src={`${audioUrl}#t=${f.timestampS}`} /> : null}
          </>)
          : <div className="wf-rv-note">Không có file âm thanh để nghe (hệ thống không lưu được).</div>) : null}
      {evs.length ? (
        <ul className="wf-rv-ev">
          {evs.map((e, i) => {
            const target = evidenceTarget(e);
            return (
              <li key={i}>
                <b>{e.frameLabel}</b> · {e.rangeLabel}
                {e.observation ? <div>{e.observation}</div> : null}
                {target ? <button type="button" className="wf-rv-shot" onClick={() => ctx.onOpen(target)} title="Mở ảnh đã gửi cho giám khảo">Xem ảnh</button> : <div className="wf-rv-note">{unopenableNote(e, ctx.snapshot)}</div>}
              </li>);
          })}
        </ul>) : null}
      {f.problems.map((p, i) => <div key={i}>{p}</div>)}
      {f.suggestion ? <div className="sug">Gợi ý: {f.suggestion}</div> : null}
    </div>
  );
};

const NO_CLIP_NOTE = 'Chưa có bản clip nào tạo xong để chấm. Tạo clip ở trên rồi quay lại.';

const WorkflowStepReviewCard: React.FC<Props> = ({ planId, eventId, stepKey, jobs, onActionDone, onPickEvidence, candidateClipId, clipLabel, controller }) => {
  const own = useStepReviews({ planId, eventId, onActionDone, enabled: !controller });
  const ctl = controller ?? own;
  const { view, loadError, pollError, busy, actionError } = ctl;
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [histOpen, setHistOpen] = useState(false);
  const [viewing, setViewing] = useState<ViewerTarget | null>(null);
  const cl = stepKey === CL_REVIEW; // P6: AI chấm clip — MỘT phiếu cho MỘT bản clip (candidateClipId), gom theo mốc giây, bấm mốc ⇒ khung hình đã trích; V5 chỉ là gợi ý

  // Đổi video / bước / bản clip đang xem ⇒ bỏ ghi chú, lịch sử mở, khung xem dở (dữ liệu + lỗi thao tác do hook lo).
  useEffect(() => { setNote(''); setNoteError(null); setHistOpen(false); setViewing(null); ctl.dismissError(); }, [planId, eventId, candidateClipId, ctl.dismissError]); // eslint-disable-line react-hooks/exhaustive-deps

  const item: StepReviewItem | null = useMemo(() => (cl ? clipItemOf(view, eventId, candidateClipId) : itemOf(view, eventId, stepKey)), [view, eventId, stepKey, cl, candidateClipId]);
  const phase = reviewPhase(item);
  const chip = verdictChip(item);
  const oldChip = phase === 'OVERDUE' ? latestChip(item) : null; // lượt mới quá hạn nhưng vẫn có phiếu xong trước đó: hiện cả hai chip
  const noClip = cl && !candidateClipId;
  const runBase = runButton(item, { busy: busy !== null, runActive: isRunActive(jobs), jobs, eventId, stepKey, candidateClipId });
  const run = noClip ? { ...runBase, enabled: false, reason: NO_CLIP_NOTE } : runBase;
  const gate = decisionGate(item);
  const r: StepReview | null = item?.latest ?? null;
  const showBody = !!r && phase !== 'RUNNING'; // đang chạy lượt mới trong hạn ⇒ chỉ nói "đang chấm", không bày kết luận cũ cạnh nút khoá
  const criteria = view?.criteria;
  const sb = stepKey === SB_REVIEW; // P5c: Duyệt B (ảnh) — thêm chip "không chặn", ảnh đã gửi, bằng chứng hình, gom theo frame; bước P3d giữ nguyên
  const snapshot = mediaSnapshotOf(r?.snapshot); // phiếu prompt có snapshot CHỮ, phiếu clip có snapshot khung hình (không có media) ⇒ không bao giờ đưa vào đường ảnh
  const pr = stepKey === PR_REVIEW; // P5d: AI chấm prompt — thêm chip "không chặn", bấm câu trích ⇒ đánh dấu trong khung prompt, gom theo shot (không neo cuộn); Đạo diễn / Quay phim / Duyệt B giữ nguyên
  const title = cl ? clipReviewTitle(clipLabel) : REVIEW_TITLE[stepKey];
  const ctx: FindingCtx = { stepKey, snapshot, onOpen: setViewing, onPick: onPickEvidence ? (quote) => onPickEvidence(promptPickOf(quote, r)) : undefined, clipReview: cl ? r : undefined, clipTitle: title, clipAudio: cl ? clipAudioOf(r) : undefined };
  const sendingThisClip = cl && !!candidateClipId && ctl.busy === 'run' && ctl.busyClipId === candidateClipId; // POST chấm clip chậm vài chục giây (BE tải video + ffmpeg trước khi trả 202)

  const onRun = async () => {
    setNote(''); setNoteError(null);
    await ctl.run(stepKey, cl ? candidateClipId ?? undefined : undefined);
  };

  const onDecide = async (decision: LeaderDecision) => {
    if (!item || !r) return;
    const bad = validateDecision(item, decision, note);
    if (bad) { setNoteError(bad); return; }
    setNoteError(null);
    if (await ctl.decide(r.id, stepKey, decision, note)) setNote('');
  };

  const columns = r && showBody ? judgeColumns(r, criteria) : [];
  const agreed = r && showBody ? agreedView(r, criteria) : [];
  const dec = r ? decisionLine(r) : null;
  const rows = item ? historyRows(item) : [];
  const nonBlock = r && showBody ? nonBlockingChip(stepKey, r) : null;
  const media = sb && r && showBody ? mediaRows(snapshot) : [];

  return (
    <section className="wf-rv" aria-label={title} data-testid="wf-rv">
      <div className="wf-rv-h">
        <b>{title}</b>
        {chip ? <span className={`wf-rv-chip ${chip.tone}`}>{chip.label}</span> : null}
        {oldChip ? <span className={`wf-rv-chip ${oldChip.tone}`} title="Phiếu đã xong gần nhất">Phiếu trước: {oldChip.label}</span> : null}
        {nonBlock ? <span className="wf-rv-chip warn" title={cl ? 'Mức nặng chỉ là mức trên phiếu — không khoá chọn bản, ghép tập hay tạo video' : pr ? 'Mức nặng chỉ là mức trên phiếu — không khoá dựng lại prompt, gen lại hay tạo video' : 'Mức nặng chỉ là mức trên phiếu — không khoá Duyệt storyboard, dựng prompt hay tạo video'}>{nonBlock}</span> : null}
        {staleNote(item) ? <span className="wf-rv-stale">{staleNote(item)}</span> : null}
        <span className="sp" />
        <button type="button" className="wf-btn" disabled={!view || !run.enabled} title={run.reason} onClick={() => { void onRun(); }}>{run.label}</button>
      </div>

      {!view && !loadError ? <div className="wf-rv-note" role="status">Đang tải phiếu…</div> : null}
      {loadError ? <div className="wf-alert ERROR" role="alert">{loadError} <button type="button" className="wf-btn" onClick={() => { void ctl.load(); }}>Tải lại</button></div> : null}
      {pollError ? <div className="wf-alert WARN" role="status">Không cập nhật được trạng thái chấm. <button type="button" className="wf-btn" onClick={() => ctl.reloadPolling()}>Tải lại</button></div> : null}
      {actionError ? (
        <div className="wf-alert ERROR" role="alert">{actionError.text}
          <button type="button" className="wf-btn ghost" onClick={() => ctl.dismissError()}>Đóng</button>
        </div>) : null}

      {sb ? <div className="wf-rv-note" data-testid="wf-rv-nonblocking">{STORYBOARD_REVIEW_NOTE}</div> : null}
      {pr ? <div className="wf-rv-note" data-testid="wf-rv-nonblocking">{PROMPT_REVIEW_NOTE}</div> : null}
      {cl ? <div className="wf-rv-note" data-testid="wf-rv-nonblocking">{CLIP_REVIEW_NOTE}</div> : null}
      {view && phase === 'NONE' && !noClip ? <div className="wf-rv-note">{sb ? 'Chưa chấm ảnh storyboard. Hai giám khảo AI xem ảnh thật và chấm mù, bạn đọc rồi phân xử.' : pr ? 'Chưa chấm prompt. Hai giám khảo AI soát câu lệnh gửi engine và danh sách ảnh đính, chấm mù, bạn đọc rồi phân xử.' : cl ? 'Chưa chấm bản clip này. Hai giám khảo AI xem khung hình trích từ clip (và nghe âm thanh), chấm mù, bạn đọc rồi phân xử.' : 'Chưa chấm bước này. Hai giám khảo AI sẽ chấm mù, bạn đọc rồi phân xử.'}</div> : null}
      {phase === 'RUNNING' ? <div className="wf-rv-note" role="status">2 giám khảo đang chấm… (tự cập nhật, không cần F5)</div> : null}
      {phase === 'OVERDUE' ? <div className="wf-alert WARN">Lượt chấm mới nhất chạy quá 15 phút nên coi là dở dang (không còn chờ).{r ? ' Bên dưới là phiếu xong gần nhất.' : ''} Bấm Chấm lại để chạy lượt mới.</div> : null}
      {sendingThisClip ? <div className="wf-rv-note wf-rv-wait" role="status"><span className="wf-rv-spin" aria-hidden="true" /> Đang tải clip và trích khung hình — có thể mất vài chục giây, đừng bấm lại.</div> : null}
      {view && run.reason && !run.enabled && phase !== 'RUNNING' && !sendingThisClip ? <div className="wf-rv-note">{run.reason}</div> : null}

      {r && showBody ? (<>
        {writerLine(r) ? <div className="wf-rv-note">{writerLine(r)}</div> : null}
        {cl && ctx.clipAudio?.status === 'NO_TRACK' ? <div className="wf-rv-note" data-testid="wf-rv-notrack">Clip không có tiếng — không chấm âm thanh / thoại (V4).</div> : null}

        {media.length ? (
          <details className="wf-rv-sec wf-rv-media" data-testid="wf-rv-media">
            <summary>Ảnh đã gửi cho giám khảo ({media.length})</summary>
            <ul>
              {media.map((m) => (
                <li key={m.mediaId}>
                  {m.order}. {m.roleLabel} · {m.mime} · {m.sizeLabel}{m.shaShort ? ` · sha ${m.shaShort}` : ''}{' '}
                  {m.src ? <button type="button" className="wf-rv-shot" onClick={() => setViewing({ src: m.src as string, title: m.roleLabel, lines: [`Ảnh thứ ${m.order} trong bộ gửi cho giám khảo.`], region: null })}>Xem ảnh</button> : null}
                </li>))}
            </ul>
          </details>) : null}

        {agreed.length ? (
          <div className="wf-rv-sec">
            <h4>Lỗi cả hai AI cùng thấy</h4>
            {agreed.map((f, i) => <Finding key={`${f.code}-${sb ? f.frameNo : cl ? f.timestampS : f.shotOrder}-${i}`} f={f} ctx={ctx} showShot />)}
          </div>) : null}

        <div className="wf-rv-sec">
          <div className="wf-rv-cols">
            {columns.map((c) => (
              <div className="wf-rv-col" key={c.label} data-testid="wf-rv-col">
                <h5>{c.label}</h5>
                <div className="model">{c.model || '—'}</div>
                {c.failed ? <div className="wf-alert ERROR">Giám khảo này không chấm xong{c.error ? `: ${c.error}` : '.'}</div> : null}
                {!c.failed && c.summary ? <div className="wf-rv-note">{c.summary}</div> : null}
                {!c.failed && c.findings.length === 0 ? <div className="wf-rv-note">Không nêu lỗi nào{c.passed.length ? ` · đạt: ${c.passed.join(', ')}` : ''}.</div> : null}
                {c.coverage ? <div className="wf-rv-note">{c.coverage}</div> : null}
                {sb
                  ? groupByFrame(c.findings).map((g) => (
                    <div className="wf-rv-grp" key={g.frameNo}>
                      <span className="wf-rv-frame">{g.label}</span>
                      {g.findings.map((f, i) => <Finding key={`${f.code}-${i}`} f={f} ctx={ctx} />)}
                    </div>))
                  : cl
                    ? groupByTime(c.findings).map((g) => (
                      <div className="wf-rv-grp" key={String(g.timestampS)}>
                        <span className="wf-rv-frame">{g.label}</span>
                        {g.findings.map((f, i) => <Finding key={`${f.code}-${i}`} f={f} ctx={ctx} source={c.role} />)}
                      </div>))
                    : groupByShot(c.findings).map((g) => (
                      <div className="wf-rv-grp" key={g.shotOrder}>
                        <ShotLink f={{ shotOrder: g.shotOrder, shotLabel: g.label }} plain={pr} />
                        {g.findings.map((f, i) => <Finding key={`${f.code}-${i}`} f={f} ctx={ctx} />)}
                      </div>))}
              </div>))}
          </div>
        </div>

        <div className="wf-rv-note">{reviewCostLine(r)}</div>

        <div className="wf-rv-sec">
          {dec ? (
            <div>
              <b>Đã phân xử: {dec.label}</b> <span className="wf-rv-note">· {dec.who}{dec.at ? ` · ${when(dec.at)}` : ''}</span>
              {dec.note ? <div style={{ marginTop: 4, overflowWrap: 'anywhere' }}>{dec.note}</div> : null}
            </div>
          ) : gate.canDecide ? (
            <div className="wf-rv-dec">
              <label className="wf-rv-note" htmlFor={`wf-rv-note-${stepKey}`}>Ghi chú{gate.noteRequired ? ' (bắt buộc — phiếu có lỗi)' : ' (không bắt buộc)'}</label>
              <textarea id={`wf-rv-note-${stepKey}`} value={note} disabled={busy !== null} onChange={(e) => { setNote(e.target.value); setNoteError(null); }} placeholder="Lý do bạn chấp nhận / yêu cầu sửa…" />
              {noteError ? <div className="wf-alert WARN" role="alert">{noteError}</div> : null}
              <div className="row">
                <button type="button" className="wf-btn primary" disabled={busy !== null} onClick={() => { void onDecide('ACCEPT'); }}>Chấp nhận kết luận</button>
                <button type="button" className="wf-btn" disabled={busy !== null} onClick={() => { void onDecide('NEEDS_FIX'); }}>Cần sửa</button>
              </div>
            </div>
          ) : <div className="wf-rv-note">{gate.reason}</div>}
        </div>
      </>) : null}

      {view ? <div className="wf-rv-note">{budgetLine(budgetFor(view, stepKey), stepKey)}</div> : null}

      {rows.length ? (
        <div className="wf-rv-hist">
          <button type="button" className="wf-btn ghost" aria-expanded={histOpen} onClick={() => setHistOpen((o) => !o)}>{histOpen ? 'Ẩn' : 'Xem'} {rows.length} phiếu trước</button>
          {histOpen ? (
            <ul>{rows.map((h) => (
              <li key={h.id}>{when(h.createdAt)} · {h.verdictLabel}{h.decidedLabel ? ` · ${h.decidedLabel}` : ''} · {h.cost}</li>))}
            </ul>) : null}
        </div>) : null}

      {viewing ? <ReviewMediaViewer target={viewing} onClose={() => setViewing(null)} /> : null}
    </section>
  );
};

export default WorkflowStepReviewCard;
